import React, { useState, useEffect, useCallback } from 'react';
import { Globe, ChevronDown } from 'lucide-react';
import {
  setUIStrings, initTranslationCache,
  t, setLanguage, getCurrentLanguage,
  useLanguage, useTranslation, translateText,
} from '../contexts/LanguageContext';

const LANGUAGES = [
  { code: 'id', name: 'Indonesia', flag: 'ID' },
  { code: 'en', name: 'English', flag: 'EN' },
  { code: 'ms', name: 'Melayu', flag: 'MY' },
  { code: 'ar', name: 'العربية', flag: 'AR' },
  { code: 'zh', name: '中文', flag: 'ZH' },
  { code: 'ja', name: '日本語', flag: 'JA' },
  { code: 'ko', name: '한국어', flag: 'KO' },
  { code: 'hi', name: 'हिन्दी', flag: 'HI' },
  { code: 'th', name: 'ไทย', flag: 'TH' },
  { code: 'vi', name: 'Tiếng Việt', flag: 'VN' },
  { code: 'fr', name: 'Français', flag: 'FR' },
  { code: 'de', name: 'Deutsch', flag: 'DE' },
  { code: 'es', name: 'Español', flag: 'ES' },
  { code: 'pt', name: 'Português', flag: 'PT' },
  { code: 'ru', name: 'Русский', flag: 'RU' },
  { code: 'tr', name: 'Türkçe', flag: 'TR' },
  { code: 'ur', name: 'اردو', flag: 'UR' },
  { code: 'bn', name: 'বাংলা', flag: 'BN' },
];

// ========== COMPLETE UI STRINGS ==========
export const UI_STRINGS = {
  // --- Navigation (Blog pages) ---
  nav_blog: 'Blog',
  nav_buku: 'Buku',
  nav_komunitas: 'Komunitas',
  nav_subscribe: 'Subscribe',

  // --- Blog Page - Hero ---
  hero_subtitle: 'Behavioral Finance & Wealth Logic',
  hero_desc: 'Berhenti mengikut arus. Mulai pahami logika di balik setiap keputusan tentang uang dan kekayaan. Di sini kita belajar bersama — membongkar bias, mempertajam intuisi finansial, dan membangun cara berpikir yang membuat Anda selangkah lebih depan.',
  pillar_bf_title: 'Behavioral Finance',
  pillar_bf_desc: 'Kenali jebakan pikiran dalam keputusan uang',
  pillar_wl_title: 'Wealth Logic',
  pillar_wl_desc: 'Pahami logika membangun kekayaan sejati',
  pillar_rm_title: 'Risk Mindset',
  pillar_rm_desc: 'Kuasai seni berpikir jernih soal risiko',

  // --- Blog Page - Content ---
  latest_articles: 'Artikel Terbaru',
  all_articles: 'Semua Artikel',
  search_results: 'Hasil Pencarian',
  search_placeholder: 'Cari artikel...',
  searching: 'Mencari...',
  no_results: 'Tidak ada hasil',
  try_other_keywords: 'Coba kata kunci lain atau',
  view_all_articles: 'Lihat Semua Artikel',
  articles_coming_soon: 'Artikel Segera Hadir',
  articles_coming_desc: 'Konten-konten yang akan mengubah cara Anda memandang uang dan keputusan finansial sedang disiapkan.',
  subscribe_for_updates: 'Subscribe untuk Update',
  loading_articles: 'Memuat artikel...',
  sort_newest: 'Terbaru',
  sort_oldest: 'Terlama',
  sort_popular: 'Paling Dibaca',
  sort_liked: 'Paling Disukai',
  sort_comments: 'Paling Banyak Komentar',
  btn_read_more: 'Baca Selengkapnya',
  btn_share: 'Bagikan Artikel',

  // --- Blog Page - Book promo ---
  book_latest: 'Buku Terbaru',
  book_desc: 'Eksplorasi mendalam tentang bagaimana kebiasaan kecil membentuk keputusan besar dalam dunia keuangan syariah.',
  book_more: 'Selengkapnya',

  // --- Blog Page - Subscribe popup ---
  subscribe_title: 'Subscribe Newsletter',
  subscribe_desc: 'Bergabunglah dengan komunitas yang belajar berpikir cerdas tentang uang, keputusan finansial, dan wealth building — langsung ke inbox Anda.',
  subscribe_email_placeholder: 'Masukkan email Anda',
  subscribe_weekly_digest: 'Terima Weekly Digest (ringkasan artikel mingguan)',
  subscribe_submitting: 'Mendaftar...',
  subscribe_button: 'Berlangganan',

  // --- Article Page ---
  back_to_blog: 'Kembali ke Blog',
  translating: 'Menerjemahkan...',
  views: 'views',
  likes: 'suka',
  comments_label: 'komentar',
  share_article: 'Bagikan Artikel:',
  comments_title: 'Komentar',
  leave_comment: 'Tinggalkan Komentar',
  name_placeholder: 'Nama Anda',
  email_placeholder: 'Email Anda',
  comment_placeholder: 'Tulis komentar Anda...',
  submit_comment: 'Kirim Komentar',
  submitting_comment: 'Mengirim...',
  article_not_found: 'Artikel tidak ditemukan',
  viewed_by: 'Dilihat oleh',
  loading: 'Memuat...',
  no_viewer_data: 'Belum ada data pengunjung.',
  viewed_times: 'dilihat',
  viewed_once: 'Dilihat',

  // --- Tag Page ---
  articles_found: 'artikel ditemukan',
  no_articles: 'Belum ada artikel',
  no_articles_with_tag: 'Tidak ada artikel dengan tag',
  all_tags: 'Semua Tag',

  // --- Footer (shared) ---
  footer_copyright: 'Tim Kreatif Pakde Heru. All rights reserved.',

  // --- Common messages ---
  comment_success: 'Komentar berhasil ditambahkan!',
  comment_error: 'Gagal mengirim komentar',
  general_error: 'Terjadi kesalahan. Silakan coba lagi.',

  // --- Chatbot ---
  chatbot_title: 'Strategic Thinker AI',
  chatbot_subtitle: 'Tanya apa saja tentang blog ini',
  chatbot_placeholder: 'Ketik pertanyaan Anda...',
  chatbot_greeting_default: 'Halo! Saya asisten <strong>Strategic Thinker</strong>. Tanyakan apa saja tentang behavioral finance, wealth logic, atau artikel di blog ini!',
  chatbot_greeting_frequent: 'Halo! Sepertinya kamu sering menjelajahi topik <strong>{topic}</strong>. Kamu sudah membaca {count} artikel sejauh ini. Ada yang ingin kamu gali lebih dalam?',
  chatbot_greeting_returning: 'Halo! Kamu sudah membaca {count} artikel, terakhir tentang <strong>{title}</strong>. Mau diskusi lebih lanjut atau rekomendasi bacaan selanjutnya?',
  chatbot_greeting_article: 'Halo! Saya lihat kamu baru membaca <strong>{title}</strong>. Ada pertanyaan tentang topik itu, atau mau rekomendasi artikel serupa?',
  chatbot_greeting_tag: 'Halo! Kamu sedang menjelajahi topik <strong>{tag}</strong>. Mau saya bantu jelaskan lebih dalam atau rekomendasikan artikel terbaik?',
  chatbot_greeting_welcome_back: 'Selamat datang kembali! Ada topik yang ingin kamu eksplorasi hari ini?',
  chatbot_error: 'Maaf, terjadi gangguan. Silakan coba lagi.',

  // ========== LANDING PAGE ==========

  // --- LP Navigation ---
  lp_nav_home: 'Halaman Utama',
  lp_nav_newsletter: 'Newsletter',
  lp_nav_buy: 'Beli Buku',

  // --- LP Hero ---
  lp_hero_label: 'Kepemimpinan Berkah untuk Bankir Syariah',
  lp_hero_subtitle: 'Transformasi Kepemimpinan Syariah Dimulai dari Kebiasaan Kecil',
  lp_hero_desc: 'Menggabungkan wisdom dari Atomic Habits dan Leading with Emotional Courage dalam nafas Islami—panduan praktis untuk membangun integritas, amanah, dan kepemimpinan yang penuh barakah.',
  lp_hero_email_ph: 'Masukkan email Anda',
  lp_hero_submitting: 'Mengirim...',
  lp_hero_cta: 'Dapatkan Preview Gratis',
  lp_hero_pages: 'Halaman',
  lp_hero_chapters: 'Bab',
  lp_hero_experts: 'Penulis Expert',
  lp_hero_zoom: 'Klik untuk zoom',
  lp_hero_quote: '"Jika kamu berani merasa, kamu akan berani memimpin."',

  // --- LP Social Proof ---
  lp_sp_joined: 'orang sudah bergabung',
  lp_sp_downloads: 'download hari ini',
  lp_sp_recent: 'bergabung sejam terakhir',

  // --- LP Pain Points ---
  lp_pain_headline: 'Kinerja Individu Menentukan Masa Depan Perbankan Syariah',
  lp_pain_desc: 'Dalam industri perbankan syariah yang terus berkembang, tekanan untuk mencapai target sering kali membuat kita lupa pada esensi: kepemimpinan yang diberkahi dan penuh integritas.',
  lp_pain_1_title: 'Tekanan Target yang Mengorbankan Nilai',
  lp_pain_1_desc: 'Tuntutan mencapai angka sering membuat pemimpin dan karyawan terjebak dalam praktik yang menjauh dari prinsip syariah',
  lp_pain_2_title: 'Kepemimpinan Tanpa Keberanian Emosional',
  lp_pain_2_desc: 'Ketakutan untuk menghadapi perasaan sendiri membuat pemimpin sulit membuat keputusan yang penuh integritas',
  lp_pain_3_title: 'Performa Individual = Performa Institusi',
  lp_pain_3_desc: 'Setiap keputusan individu—dari komisaris hingga frontliner—berdampak langsung pada reputasi dan keberkahan lembaga',
  lp_pain_4_title: 'Kehilangan Makna dalam Rutinitas',
  lp_pain_4_desc: 'Pekerjaan menjadi sekadar rutinitas, bukan ibadah yang penuh niat dan membawa ridha Allah',
  lp_pain_highlight_title: 'Realita yang Tidak Bisa Diabaikan',
  lp_pain_highlight_1: 'Setiap keputusan yang Anda buat—dari menyetujui pembiayaan hingga memimpin rapat tim—berdampak langsung pada reputasi institusi, kepercayaan nasabah, dan yang terpenting: barakah dalam rezeki umat.',
  lp_pain_highlight_2a: 'Pertanyaannya bukan "Apakah kita bisa mencapai target?"',
  lp_pain_highlight_2b: 'Tetapi: "Apakah cara kita mencapai target itu penuh dengan amanah dan ridha Allah?"',

  // --- LP Solution ---
  lp_sol_headline: 'Transformasi Dimulai dari Kebiasaan Kecil yang Penuh Berkah',
  lp_sol_desc: 'Buku ini memandu Anda membangun kebiasaan atomic yang mengubah cara Anda memimpin—dari dalam ke luar, dari niat hingga tindakan.',
  lp_sol_1_title: 'Kepemimpinan Berbasis Keberanian Emosional',
  lp_sol_1_desc: 'Pelajari bagaimana menghadapi perasaan dengan jujur sambil tetap teguh pada prinsip syariah',
  lp_sol_2_title: 'Framework Atomic Habits untuk Bankir Syariah',
  lp_sol_2_desc: 'Sistem praktis membangun kebiasaan kecil yang konsisten: dari menulis niat setiap pagi hingga refleksi malam',
  lp_sol_3_title: 'Integritas sebagai Gunung, Bukan Slogan',
  lp_sol_3_desc: 'Langkah-langkah konkret menjaga amanah bahkan di tengah tekanan target dan godaan material',
  lp_sol_4_title: 'Dari Pekerjaan Menjadi Ibadah Penuh Barakah',
  lp_sol_4_desc: 'Mengubah setiap keputusan dan interaksi menjadi jembatan menuju ridha Ilahi',
  lp_sol_framework: '4 Bagian Transformatif',
  lp_sol_fw1_title: 'Membangun Kepercayaan Diri',
  lp_sol_fw1_desc: 'Dari amanah pada diri sendiri hingga ketenangan di tengah tekanan',
  lp_sol_fw2_title: 'Terhubung dengan Orang Lain',
  lp_sol_fw2_desc: 'Mendengarkan dengan rasa, bicara kebenaran dengan adab',
  lp_sol_fw3_title: 'Berkomitmen pada Tujuan',
  lp_sol_fw3_desc: 'Menyelaraskan tujuan pribadi dengan misi lembaga dan ridha Allah',
  lp_sol_fw4_title: 'Menumbuhkan Keberanian Emosional',
  lp_sol_fw4_desc: 'Menjaga nyala perubahan dan membangun budaya istiqamah dalam tim',

  // --- LP Preview ---
  lp_preview_title: 'Rasakan Transformasinya',
  lp_preview_desc: 'Baca preview chapter dan mulai perjalanan kepemimpinan berkah Anda',
  lp_preview_less: 'Tampilkan Lebih Sedikit',
  lp_preview_more: 'Baca Selengkapnya',
  lp_preview_dl_title: 'Download Preview Chapter Lengkap',
  lp_preview_dl_desc: 'Dapatkan PDF lengkap Bab 1 + bonus checklist "Atomic Behavior" yang bisa langsung Anda terapkan',
  lp_preview_dl_email: 'Email Anda',
  lp_preview_dl_submitting: 'Mengirim...',
  lp_preview_dl_btn: 'Download Gratis',
  lp_preview_dl_note: '* Kami menghormati privasi Anda. Email hanya untuk mengirim preview dan konten eksklusif.',

  // --- LP Testimonial ---
  lp_test_headline: 'Apa Kata Para Ahli Islamic Finance Dunia',
  lp_test_desc: 'Dipuji oleh thought leaders dan praktisi terkemuka di industri perbankan syariah global',
  lp_test_cta: 'Bergabunglah dengan para pemimpin perbankan syariah yang telah memulai transformasi mereka',

  // --- LP Benefits ---
  lp_ben_headline: 'Apa yang Akan Anda Dapatkan?',
  lp_ben_1_title: '15 Bab Transformatif',
  lp_ben_1_desc: 'Panduan lengkap dari membangun kepercayaan diri hingga mewariskan kepemimpinan yang berkah',
  lp_ben_2_title: 'Atomic Behavior di Setiap Bab',
  lp_ben_2_desc: 'Kebiasaan praktis yang bisa langsung diterapkan: niat sebelum rapat, checklist 5 menit, refleksi harian',
  lp_ben_3_title: 'Latihan Reflektif Berbasis Spiritual',
  lp_ben_3_desc: 'Pertanyaan mendalam untuk menggali niat, mengukur integritas, dan merenungkan keberkahan',
  lp_ben_4_title: 'Kisah Nyata Bankir Syariah',
  lp_ben_4_desc: 'Cerita inspiratif dari Pak Ridwan, Lina, Farid, Rizal yang menghadapi dilema etika di lapangan',
  lp_ben_5_title: 'Framework Kepemimpinan Inside-Out',
  lp_ben_5_desc: 'Dari self-leadership hingga team culture: membangun organisasi yang istiqamah dan penuh amanah',
  lp_ben_6_title: 'Integrasi Hikmah Dua Karya Besar',
  lp_ben_6_desc: 'Menggabungkan Atomic Habits (James Clear) dan Leading with Emotional Courage (Peter Bregman) dalam nafas Islami',
  lp_ben_highlight_title: 'Lebih dari Sekadar Buku',
  lp_ben_highlight_desc: 'Ini adalah sistem transformasi yang telah mengintegrasikan wisdom dua karya besar dunia dengan nilai-nilai Islam—dirancang khusus untuk tantangan unik yang dihadapi para pemimpin dan profesional perbankan syariah.',
  lp_ben_feat_1: 'Praktis & Langsung Diterapkan',
  lp_ben_feat_2: 'Berbasis Riset & Pengalaman Lapangan',
  lp_ben_feat_3: 'Spiritual & Profesional',

  // --- LP CTA ---
  lp_cta_headline: 'Jika Kamu Berani Merasa, Kamu Akan Berani Memimpin',
  lp_cta_desc: 'Dapatkan buku ini sekarang dan mulai perjalanan transformasi kepemimpinan Anda',
  lp_cta_price_label: 'Investasi untuk Transformasi Anda:',
  lp_cta_price_note: 'Tersedia di Google Books',
  lp_cta_btn: 'Beli Sekarang di Google Books',
  lp_cta_guarantee_1: 'Akses selamanya ke 144 halaman konten transformatif',
  lp_cta_guarantee_2: 'Baca di semua device: web, tablet, smartphone',
  lp_cta_guarantee_3: 'Dari penerbit Karyanirwasita Kreatif Media',
  lp_cta_badge: 'Best Investment',
  lp_cta_badge_sub: 'untuk Kepemimpinan Berkah',
  lp_cta_urgency: 'Ribuan bankir syariah telah memulai transformasi mereka. Jangan tunda lagi—setiap hari tanpa buku ini adalah hari yang hilang untuk membangun kepemimpinan yang penuh barakah.',

  // --- LP Newsletter ---
  lp_nl_title: 'Dapatkan Email Series Eksklusif tentang Kepemimpinan Berkah',
  lp_nl_desc: 'Daftar sekarang dan terima 4 email transformatif yang akan membimbing Anda membangun kepemimpinan yang penuh integritas, amanah, dan barakah.',
  lp_nl_seq_title: 'Yang Akan Anda Terima:',
  lp_nl_day: 'Hari',
  lp_nl_email_ph: 'Masukkan email Anda',
  lp_nl_submitting: 'Mendaftar...',
  lp_nl_btn: 'Daftar Sekarang',
  lp_nl_note: 'Gratis, tanpa spam. Anda bisa berhenti berlangganan kapan saja. Kami berkomitmen menjaga privasi data Anda.',

  // --- LP Contact ---
  lp_contact_title: 'Hubungi Kami',
  lp_contact_desc: 'Punya pertanyaan tentang buku atau ingin mengetahui lebih lanjut? Kami siap membantu Anda.',
  lp_contact_info: 'Informasi Kontak',
  lp_contact_purchase: 'Untuk Pembelian Buku:',
  lp_contact_purchase_desc: 'Buku tersedia eksklusif di',
  lp_contact_name_label: 'Nama Lengkap',
  lp_contact_name_ph: 'Nama Anda',
  lp_contact_subject_label: 'Subjek',
  lp_contact_subject_ph: 'Topik pesan Anda',
  lp_contact_msg_label: 'Pesan',
  lp_contact_msg_ph: 'Tulis pesan Anda di sini...',
  lp_contact_submitting: 'Mengirim...',
  lp_contact_btn: 'Kirim Pesan',

  // --- LP Social Proof ---
  // (already above)

  // --- LP Exit Intent ---
  lp_exit_title: 'Tunggu Dulu!',
  lp_exit_subtitle: 'Jangan lewatkan penawaran spesial ini',
  lp_exit_badge: 'PENAWARAN TERBATAS',
  lp_exit_offer: 'Dapatkan Preview Chapter GRATIS + Bonus Email Series Eksklusif',
  lp_exit_b1: 'Preview Bab 1: "Jadilah Dirimu yang Amanah"',
  lp_exit_b2: '4 Email Series Transformatif (7 hari)',
  lp_exit_b3: 'Checklist "Atomic Behavior" yang Praktis',
  lp_exit_b4: 'Insight dari Tokoh Islamic Finance Dunia',
  lp_exit_email_ph: 'Masukkan email Anda',
  lp_exit_btn: 'Dapatkan Sekarang GRATIS',
  lp_exit_note: 'Setelah itu, Anda bisa langsung beli buku lengkap hanya $11.99 di Google Books',
  lp_exit_guarantee: '100% Gratis. Tanpa spam. Bisa unsubscribe kapan saja.',

  // --- Author Page ---
  author_biography: 'Biografi',
  author_education: 'Riwayat Pendidikan',
  author_career: 'Riwayat Karir',
  author_expertise: 'Keahlian',
  author_other_authors: 'Penulis Lainnya',
  author_domicile: 'Domisili',
  author_period: 'Periode',
  author_view_bpkh: 'Lihat Biodata Resmi di BPKH',
  author_not_found: 'Penulis tidak ditemukan',
  author_translating: 'Menerjemahkan konten...',

  // --- LP Footer ---
  lp_footer_about: 'Tentang Buku',
  lp_footer_pages_suffix: 'halaman transformasi kepemimpinan',
  lp_footer_authors: 'Para Penulis',
  lp_footer_more: 'penulis lainnya',
  lp_footer_less: 'Tutup',
  lp_footer_links: 'Quick Links',
  lp_footer_buy_gbooks: 'Beli di Google Books',
  lp_footer_subscribe: 'Subscribe Newsletter',
  lp_footer_contact_us: 'Hubungi Kami',
  lp_footer_connect: 'Connect',
  lp_footer_copyright: '© 2026 Tim Kreatif Pakde Heru. All rights reserved.',
  lp_footer_tagline: 'Dibuat dengan doa untuk para pemimpin berkah',
};

// --- Initialize and re-export from context ---
setUIStrings(UI_STRINGS);
initTranslationCache();

export { t, setLanguage, getCurrentLanguage, useLanguage, useTranslation, translateText };
export { LanguageProvider, useLanguageContext } from '../contexts/LanguageContext';

// Language Picker Component
const LanguagePicker = () => {
  const currentLang = useLanguage();
  const [open, setOpen] = useState(false);
  const current = LANGUAGES.find(l => l.code === currentLang);

  const handleSelect = useCallback((code) => {
    setLanguage(code);
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!e.target.closest('.lang-picker')) setOpen(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [open]);

  return (
    <div className="lang-picker" data-testid="lang-picker">
      <button
        className="lang-picker-btn"
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        data-testid="lang-picker-btn"
      >
        <Globe size={14} />
        <span>{current?.flag || 'ID'}</span>
        <ChevronDown size={12} style={{ opacity: 0.5, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>
      {open && (
        <div className="lang-picker-dropdown" data-testid="lang-picker-dropdown">
          {LANGUAGES.map(l => (
            <button
              key={l.code}
              className={`lang-option ${l.code === currentLang ? 'active' : ''}`}
              onClick={() => handleSelect(l.code)}
              data-testid={`lang-option-${l.code}`}
            >
              <span className="lang-flag">{l.flag}</span>
              <span className="lang-name">{l.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguagePicker;
